import { useCallback, useRef } from "react";
import * as onboardingApi from "@/api/onboarding";
import { isWebOnboardingStep } from "@/lib/onboarding-step-owner";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { OnboardingDoneStep } from "./onboarding-done-step";
import { OnboardingWelcomeStep } from "./onboarding-welcome-step";

type WebStep = "welcome" | "done";

function resolveWebStep(step: string): WebStep {
  if (
    step === "fresh" ||
    step === "orient" ||
    step === "tour" ||
    step === "tour_done"
  ) {
    return "welcome";
  }
  return "done";
}

/**
 * Browser fallback onboarding. The desktop app owns its richer permission
 * sequence; browser users only need a welcome step and a clear completion
 * boundary. The retired companion install step (`companion_install`,
 * `install`) and legacy demo steps intentionally collapse to `done`.
 */
export function OnboardingOverlay() {
  const step = useOnboardingStore((s) => s.step);
  const stepHydrated = useOnboardingStore((s) => s.stepHydrated);
  const advanceStep = useOnboardingStore((s) => s.advanceStep);
  const savedNameRef = useRef<string | null>(null);

  const completeOnboarding = useCallback(() => {
    const name = savedNameRef.current;
    if (name) {
      onboardingApi.updateStepWithName("completed", name).catch(() => {});
    }
    void advanceStep("completed");
  }, [advanceStep]);

  if (!stepHydrated || step === null || !isWebOnboardingStep(step)) {
    return null;
  }

  const webStep = resolveWebStep(step);

  if (webStep === "welcome") {
    return (
      <OnboardingWelcomeStep
        onContinue={(name) => {
          savedNameRef.current = name || null;
          void advanceStep("demo_done");
        }}
        onSkip={completeOnboarding}
      />
    );
  }

  return <OnboardingDoneStep onGetStarted={completeOnboarding} />;
}
